/* eslint-disable react/destructuring-assignment */
import React, { ChangeEvent, ReactElement, FocusEvent, useRef } from 'react';
import styled, { css } from 'styled-components';
import Color from '@/utils/color';

type CssProps = {
  error: boolean;
  hasText: boolean;
};

const textStyle = css<CssProps>`
  border-bottom: 1px solid
    ${({ error }) => (error ? Color.Yellow : Color.CoolGrey)};
  min-width: 0;
  width: 100%;
  font-size: 1rem;
  color: ${({ error, theme }) =>
    error ? Color.Yellow : (theme as any).color.text};
  caret-color: ${({ error, theme }) =>
    error ? Color.Yellow : (theme as any).color.primary};
  background: transparent;
  &:active,
  &:focus {
    border-color: ${({ error, theme }) =>
      error ? Color.Yellow : (theme as any).color.primary};
    & + span {
      transform: translateY(-100%);
      top: 0;
      font-size: 0.7rem;
      color: ${({ error, theme }) =>
        error ? Color.Yellow : (theme as any).color.primary};
    }
  }
  &:not(:focus) {
    & + span {
      font-size: 0.7rem;
      top: 0;
      ${(props) => props.hasText && 'transform: translateY(-100%)'};
      color: ${({ error }) => (error ? Color.Yellow : Color.Grey)};
    }
  }
`;

const BaseTextarea = styled.textarea`
  border: ${Color.CoolGrey} solid 1px;
  ${textStyle as any}
  padding: 5px;
  width: 100%;
`;

const BaseTextInput = styled.input`
  border-top-width: 0;
  border-left-width: 0;
  border-right-width: 0;
  ${textStyle as any}
`;

type LabelProps = {
  disabled: boolean;
};
const Label = styled.div<LabelProps>`
  position: relative;
  opacity: ${(props) => (props.disabled ? 0.3 : 1.0)};
`;

type PlaceholderProps = {
  stickToTop?: boolean;
};

const Placeholder = styled.span<PlaceholderProps>`
  position: ${(props) => (props.stickToTop ? 'inherited' : 'absolute')};
  bottom: 0.3rem;
  left: 0;
  transition: 0.2s ease;
  color: ${Color.Grey};
  font-size: 0.8rem;
  white-space: pre;
`;

type TextInputProps = {
  required?: Boolean;
  placeholder: ReactElement | string;
  error?: ReactElement | string | null;
  multiline?: Boolean;
  value: any;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onChangeText?: (text: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void | undefined;
};

function TextInput({
  required = false,
  placeholder = '',
  value = '',
  error = null,
  multiline = false,
  disabled = false,
  onChange,
  onChangeText,
  onBlur,
  ...rest
}: TextInputProps) {
  const inputRef = useRef<HTMLInputElement>(undefined);

  let placeholderText = error || placeholder;
  if (typeof error === 'boolean') {
    placeholderText = placeholder;
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    onChange(e);
    if (onChangeText) {
      onChangeText(e.target.value);
    }
  }

  function focus() {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }

  const InputElement: any = multiline ? BaseTextarea : BaseTextInput;

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (onBlur) {
      onBlur(e);
    }

    window.scrollBy(0, 0);
  }

  return (
    <Label disabled={disabled} {...rest}>
      {multiline && (
        <Placeholder onClick={focus} stickToTop>
          {placeholderText}
          {required && ' *'}
        </Placeholder>
      )}
      {/** @ts-ignore */}
      <InputElement
        disabled={disabled}
        type="text"
        {...rest}
        ref={inputRef}
        error={error}
        multiline
        placeholder=""
        hasText={!!value || value === 0}
        value={value || ''}
        required={required}
        onBlur={handleBlur}
        onChange={handleChange}
      />
      {!multiline && (
        <Placeholder onClick={focus}>
          {placeholderText}
          {required && ' *'}
        </Placeholder>
      )}
    </Label>
  );
}

export default TextInput;
